<template>
  <div
    class="product-gallery-zoom"
    :class="{ 'is-zoom-active': states.zoomActive }"
  >
    <div
      v-if="images"
      class="product-gallery-zoom__carousel splide"
      js-carousel="ProductGalleryZoom"
    >
      <div class="product-gallery-zoom__carousel-track splide__track">
        <ul class="splide__list">
          <li
            v-for="(image, index) in images"
            :key="`${image.id}-${index}-${image.variant}-Zoom`"
            class="product-gallery-zoom__slide splide__slide"
          >
            <div class="product-gallery-zoom__main-image-container">
              <div class="product-gallery-zoom__main-image">
                <responsive-image
                  block-class="product-gallery-zoom"
                  :image="image"
                  ratio="1:1"
                  :lazypreload="true"
                  sizes="100vh"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div
        v-show="showArrows"
        class="product-gallery-zoom__arrows splide__arrows"
      >
        <button
          ref="arrowPrev"
          class="splide__arrow splide__arrow--secondary splide__arrow--prev"
          :aria-label="$string('accessibility.carousel.previous')"
        >
          <icon-previous />
        </button>

        <button
          ref="arrowNext"
          class="splide__arrow splide__arrow--secondary splide__arrow--next"
          :aria-label="$string('accessibility.carousel.next')"
        >
          <icon-next />
        </button>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product gallery zoom (product-gallery-zoom)
 * -----------------------------------------------------------------------------
 * A carousel component for the Product Gallery displaying zoomed-in images.
 * This is not using any zoomed plugins.
 * @param {Number} currentIndex - default/current images index for the carousel.
 * @param {Array} [images] - Array of images from a formatted product object.
 *
 */

import Carousel from '~/api/carousel'
import ResponsiveImage from '~global/images/responsive-image'
import IconPrevious from '~icons/direction/arrow/left.svg'
import IconNext from '~icons/direction/arrow/right.svg'

export default {
  name: 'ProductGalleryZoom',

  components: {
    ResponsiveImage,
    IconPrevious,
    IconNext,
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },
    images: {
      type: [Boolean, Array],
      default: false,
    },
    modalActive: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'gallery-closed',
    'update-gallery-index',
  ],

  data() {
    return {
      carousel: false,
      carouselConfig: {
        arrows: true,
        classes: {
          pagination: 'splide__pagination splide__pagination--light product-gallery-zoom__pagination',
          page: 'splide__pagination__page product-gallery-zoom__page',
        },
        drag: false,
        pagination: true,
        rewind: false,
        speed: 800,
        type: 'slide',
      },
      states: {
        zoomActive: false,
      },
    }
  },

  computed: {

    /**
     * Calculate whether to show carousel arrows.
     * - Don't show if there's only one image
     * @returns {Boolean}
     */
    showArrows() {
      return this.images.length > 1
    },
  },

  watch: {

    /**
     * The current index of an inline gallery has changed.
     * - Instruct the carousel to update.
     * - Use Props instead of the Splide .sync() methods for
     *   scenarios where the modal hasn't been mounted/imported.
     */
    currentIndex() {
      if (!this.carousel) {
        return
      }

      this.carousel.go(this.currentIndex)
    },

    /**
     * Watch image array changes and re-initialise carousel.
     */
    images() {

      /**
       * Reset active carousel when changing.
       */
      if (typeof this.carousel !== 'boolean') {
        this.carousel.destroy()
        this.carousel = false
      }

      this.initCarousel()
    },

    /**
     * Watch the active state of the modal.
     */
    modalActive() {
      if (this.modalActive) {
        setTimeout(() => {
          this.carousel.refresh()
        }, 0)

        return
      }

      this.$emit('gallery-closed')
      this.states.zoomActive = false
    },

    /**
     * Watch for when the user has zoomed into either:
     */
    'states.zoomActive': function() {

      /**
       * When the user has zoomed into an image, disable
       * dragging/swiping son the carousel to prevent unwanted actions.
       */
      if (typeof this.carousel !== 'boolean') {
        this.carousel.options = {
          drag: !this.states.zoomActive,
        }
      }
    },
  },

  mounted() {
    this.initCarousel()
  },

  methods: {

    /**
     * Set Event Listeners.
     */
    setEventListeners() {

      /**
       * The carousel has just begin to update the current/visible slide.
       * - Emit event up to parents to notify any other carousels.
       */
      this.carousel.on('move', () => {
        this.states.zoomActive = false
        this.$emit('update-gallery-index', this.carousel.index)
      })
    },


    /**
     * Initialise the carousel.
     */
    initCarousel() {
      this.carousel = Carousel(
      `[js-carousel="ProductGalleryZoom"]`, {
        ...this.carouselConfig,
        arrows: this.showArrows,
        start: this.currentIndex,
      },
    )

      /**
       * Set Event Listeners before `mount()` to ensure that
       * the `mounted`  event can be listened to.
       */
      this.setEventListeners()

      this.carousel.mount()
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery-zoom';
</style>
